import AddIcon from '@mui/icons-material/Add'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { Link, Typography, Box } from '@mui/material'
import { styled } from '@mui/system'
import { MAX_UPLOAD_FILES, ACCEPT_FILE_TYPES } from 'config'
import useDragUpload from 'hooks/useDragUpload'
import { ChangeEvent } from 'react'

import FileList from './FileList'
import { AddFilesHandler, DeleteFileHandler } from './types'

const MyTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}))

type StyleProps = { dragging: boolean; reachedMax: boolean }
const MyLabel = styled('label')<StyleProps>(({ reachedMax, dragging, theme }) => ({
  flex: 1,
  minHeight: theme.spacing(4),
  display: reachedMax ? 'none' : 'flex',
  flexFlow: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  backgroundColor: dragging ? theme.palette.grey[100] : theme.palette.grey[50],
  transition: 'backgroundColor .2s',
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}))

interface Props {
  onAddFiles: AddFilesHandler
  onDeleteFile: DeleteFileHandler
  files: (File & { id: number })[]
  hideFiles?: boolean
  showHint?: boolean
  narrowStyle?: boolean
}

const UploadBox = (props: Props) => {
  const { onAddFiles, onDeleteFile, files, hideFiles, showHint = true, narrowStyle } = props
  const showingFiles = !hideFiles && Boolean(files?.length)
  const reachedMax = files?.length >= MAX_UPLOAD_FILES

  const { dragging, ref } = useDragUpload({
    onDrop: (files) => onAddFiles([...files]),
  })

  const handleChangeFile = (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event
    const files = target.files!
    if (files.length !== 0) {
      onAddFiles([...files])
      target.value = ''
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexFlow: 'column',
        width: '100%',
        height: '100%',
        border: 'dashed',
        borderRadius: 2.2,
        borderColor: 'grey.200',
        overflow: 'hiddne',
      }}
    >
      {showingFiles && <FileList files={files} onDeleteFile={onDeleteFile} />}
      <MyLabel dragging={dragging} reachedMax={reachedMax} ref={ref} data-testid="upload-box">
        {!showingFiles && (
          <Box
            sx={{
              display: 'flex',
              flexFlow: 'column',
              alignItems: 'center',
              marginBottom: 1,
              textAlign: 'center',
              my: 1,
            }}
          >
            <DescriptionOutlinedIcon fontSize="large" />
            <p>
              ここにファイルをドロップ
              <br />
              または<Link>ファイルを選択</Link>
            </p>
            {showHint ? (
              <>
                <MyTypography variant="caption">
                  形式 (最大サイズ)：
                  {narrowStyle && <br />}
                  JPEG / PNG (20MB)、PDF / TIFF (100MB)
                </MyTypography>
                <MyTypography variant="caption">
                  上記形式のZIPファイルも利用可能です。
                  <br />
                  ZIPファイルの最大サイズは500MBです。
                </MyTypography>
              </>
            ) : (
              <MyTypography variant="caption">形式 (最大サイズ)：JPEG / PNG (20MB)</MyTypography>
            )}
          </Box>
        )}

        {showHint && <AddIcon color="primary" sx={{ my: 0.5 }} />}

        <input
          style={{ display: 'none' }}
          type="file"
          multiple
          accept={ACCEPT_FILE_TYPES.join(',')}
          disabled={reachedMax}
          onChange={handleChangeFile}
        />
      </MyLabel>
    </Box>
  )
}

export default UploadBox
